import {
  daysBetweenLocalDates,
  formatTrustBudDateLabel,
  parseTrustBudDate,
} from "../../shared/utils/trustbudDates";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const SHORT_DAYS = ["S", "M", "T", "W", "T", "F", "S"];

function toDateOnlyKey(value) {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = parseTrustBudDate(value);
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parsePreferredTime(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return { hour, minute };
}

function formatPreferredTime(value) {
  const parsed = parsePreferredTime(value);
  if (!parsed) return null;
  const suffix = parsed.hour < 12 ? "AM" : "PM";
  const displayHour = parsed.hour % 12 || 12;
  return `${displayHour}:${String(parsed.minute).padStart(2, "0")} ${suffix}`;
}

function plannedTimePassed(commitment, dayKey, now = new Date()) {
  if (dayKey !== toDateOnlyKey(now)) return false;
  const parsed = parsePreferredTime(commitment?.preferred_time);
  if (!parsed) return false;
  const planned = new Date(now.getFullYear(), now.getMonth(), now.getDate(), parsed.hour, parsed.minute);
  return now.getTime() > planned.getTime();
}

function startOfWeek(date = new Date()) {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = next.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  next.setDate(next.getDate() + diff);
  return next;
}

export function buildWeekDays(date = new Date()) {
  const first = startOfWeek(date);
  return Array.from({ length: 7 }).map((_, index) => {
    const current = new Date(first);
    current.setDate(first.getDate() + index);
    return {
      key: toDateOnlyKey(current),
      label: SHORT_DAYS[current.getDay()],
      weekday: current.getDay(),
      monthDay: current.getDate(),
      fullLabel: DAYS[current.getDay()],
      isToday: toDateOnlyKey(current) === toDateOnlyKey(date),
    };
  });
}

export function parseGoalDueDate(goalOrDescription = "") {
  if (goalOrDescription && typeof goalOrDescription === "object") {
    const targetDate = String(goalOrDescription.target_date || "").trim();
    if (targetDate) return targetDate;
    const description = String(goalOrDescription.description || "").trim();
    const match = description.match(/^By\s+(\d{4}-\d{2}-\d{2})$/);
    return match ? match[1] : "";
  }
  const description = String(goalOrDescription || "").trim();
  const match = description.match(/^By\s+(\d{4}-\d{2}-\d{2})$/);
  return match ? match[1] : "";
}

export function formatShortDate(value) {
  if (!value) return "";
  const label = formatTrustBudDateLabel(value);
  const date = parseTrustBudDate(value);
  if (!date) return value;
  return `${label}, ${date.getFullYear()}`;
}

export function getDaysRemaining(value) {
  if (!value) return null;
  const diff = daysBetweenLocalDates(new Date(), value);
  return diff == null ? null : diff;
}

export function buildGoalSwitchOptions(goals = []) {
  return goals
    .filter((goal) => !goal.completed_at)
    .map((goal) => ({
      id: goal.id,
      label: goal.title,
    }));
}

export function hasNumericGoalTarget(goal) {
  if (!goal) return false;
  const value = goal.target_value;
  return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
}

export function mergeGoalData(summaryGoal, detailGoal) {
  if (!summaryGoal && !detailGoal) return null;
  return {
    ...(detailGoal || {}),
    ...(summaryGoal || {}),
    commitments: detailGoal?.commitments || summaryGoal?.commitments || [],
  };
}

function uniqueCompletionKeys(completions = []) {
  const keys = new Set();
  completions.forEach((completion) => {
    if (!completion?.completed) return;
    const key = toDateOnlyKey(completion.date);
    if (key) keys.add(key);
  });
  return keys;
}

export function buildWeeklyActionRows(commitments = [], weekDays = buildWeekDays()) {
  const weekKeys = weekDays.map((day) => day.key);
  return (commitments || []).map((commitment) => {
    const setupText = buildHabitSetupText(commitment);
    const usedIn = buildHabitUsedInChips(commitment);
    const completedCount = Number(commitment?.current_window_completed_count || 0);
    const targetCount = Number(commitment?.current_window_target_count || 0);
    const completionKeys = uniqueCompletionKeys(commitment.completions || []);
    if (commitment.frequency === "daily") {
      if (targetCount > 0) {
        return {
          id: commitment.id,
          title: commitment.title,
          smallVersionTitle: commitment.minimum_version_title || "",
          intelligenceNote: commitment.intelligence_note || "",
          setupText,
          usedIn,
          progressLabel: `${completedCount} / ${targetCount} days`,
          complete: completedCount >= targetCount,
          accent: completedCount >= targetCount ? "success" : completedCount > 0 ? "active" : "default",
          commitment,
        };
      }
      const target = weekDays.filter((day) => (commitment.active_days || []).includes((day.weekday + 6) % 7)).length;
      const done = weekKeys.filter((key, index) => {
        const weekdayIndex = (weekDays[index].weekday + 6) % 7;
        return (commitment.active_days || []).includes(weekdayIndex) && completionKeys.has(key);
      }).length;
      return {
        id: commitment.id,
        title: commitment.title,
        smallVersionTitle: commitment.minimum_version_title || "",
        intelligenceNote: commitment.intelligence_note || "",
        setupText,
        usedIn,
        progressLabel: `${done} / ${Math.max(target, 1)} days`,
        complete: target > 0 && done >= target,
        accent: done > 0 ? "success" : "default",
        commitment,
      };
    }
    if (commitment.frequency === "weekly") {
      if (targetCount > 0) {
        return {
          id: commitment.id,
          title: commitment.title,
          smallVersionTitle: commitment.minimum_version_title || "",
          intelligenceNote: commitment.intelligence_note || "",
          setupText,
          usedIn,
          progressLabel: `${completedCount} / ${targetCount} this week`,
          complete: completedCount >= targetCount,
          accent: completedCount >= targetCount ? "success" : completedCount > 0 ? "active" : "default",
          commitment,
        };
      }
      const done = weekKeys.some((key) => completionKeys.has(key));
      return {
        id: commitment.id,
        title: commitment.title,
        smallVersionTitle: commitment.minimum_version_title || "",
        intelligenceNote: commitment.intelligence_note || "",
        setupText,
        usedIn,
        progressLabel: done ? "Done this week" : "Still open",
        complete: done,
        accent: done ? "success" : "default",
        commitment,
      };
    }
    if (targetCount > 0) {
      return {
        id: commitment.id,
        title: commitment.title,
        smallVersionTitle: commitment.minimum_version_title || "",
        intelligenceNote: commitment.intelligence_note || "",
        setupText,
        usedIn,
        progressLabel: `${completedCount} / ${targetCount} this month`,
        complete: completedCount >= targetCount,
        accent: completedCount >= targetCount ? "success" : completedCount > 0 ? "active" : "default",
        commitment,
      };
    }
    const done = (commitment.completions || []).some((completion) => completion?.completed);
    return {
      id: commitment.id,
      title: commitment.title,
      smallVersionTitle: commitment.minimum_version_title || "",
      intelligenceNote: commitment.intelligence_note || "",
      setupText,
      usedIn,
      progressLabel: done ? "Done this month" : "Planned this month",
      complete: done,
      accent: done ? "success" : "default",
      commitment,
    };
  });
}

function buildHabitUsedInChips(commitment) {
  const chips = ["Today", "Insights"];
  if (commitment?.preferred_time) chips.push("Coach check-ins");
  if (commitment?.cue_anchor) chips.push("Routine cue");
  if (commitment?.cue_context) chips.push("Situation cue");
  if (commitment?.cue_location_label) chips.push("Place cue");
  if (commitment?.minimum_version_title) chips.push("Keep-alive step set");
  const type = String(commitment?.semantic_profile?.commitment_type || commitment?.commitment_type || "").toLowerCase();
  const domain = String(commitment?.semantic_profile?.domain || commitment?.domain || "").toLowerCase();
  if (type.includes("meal") || type.includes("nutrition") || domain.includes("nutrition")) {
    chips.push("Before-meal check-in");
  }
  return Array.from(new Set(chips)).slice(0, 5);
}

function buildHabitSetupText(commitment) {
  const parts = [];
  const frequency = String(commitment?.frequency || "daily");
  if (frequency === "daily") {
    const activeDays = Array.isArray(commitment?.active_days) && commitment.active_days.length
      ? commitment.active_days
      : [0, 1, 2, 3, 4, 5, 6];
    parts.push(activeDays.length === 7 ? "Daily" : `${activeDays.length} day${activeDays.length === 1 ? "" : "s"}/week`);
  } else {
    parts.push(frequency.charAt(0).toUpperCase() + frequency.slice(1));
  }
  if (commitment?.preferred_time) {
    parts.push(`Planned ${formatPreferredTime(commitment.preferred_time)}`);
  } else if (commitment?.cue_location_label || commitment?.cue_anchor || commitment?.cue_context) {
    parts.push(commitment?.cue_location_label || commitment?.cue_anchor || commitment?.cue_context || "Cue set");
  } else {
    parts.push("Anytime today");
  }
  return parts.join(" · ");
}

export function buildWeekPreview(commitments = [], weekDays = buildWeekDays(), selectedDateKey = toDateOnlyKey(new Date()), now = new Date(), weekState = null, goalId = null) {
  if (Array.isArray(weekState?.days) && weekState.days.length) {
    return weekState.days.map((day) => {
      const fallback = weekDays.find((item) => item.key === day.date) || {};
      const habits = (day.habits || [])
        .filter((habit) => !goalId || Number(habit.goal_id) === Number(goalId))
        .map((habit) => weekStateHabitToPreview(habit, day.date));
      return {
        ...fallback,
        key: day.date,
        date: day.date,
        isSelected: selectedDateKey === day.date,
        dueCount: habits.length,
        doneCount: habits.filter((habit) => habit.completed).length,
        overdueCount: habits.filter((habit) => habit.overdue).length,
        habits,
        indicators: habits,
      };
    });
  }
  const visibleCommitments = (commitments || []).filter((commitment) => commitment?.is_active !== false);
  return weekDays.map((day, index) => {
    const weekdayIndex = (day.weekday + 6) % 7;
    const habits = visibleCommitments
      .map((commitment) => {
      const completionKeys = uniqueCompletionKeys(commitment.completions || []);
      const completed = completionKeys.has(day.key);
      let planned = false;

      if (commitment.frequency === "daily") {
        planned = (commitment.active_days || []).includes(weekdayIndex);
      } else if (commitment.frequency === "weekly") {
        const weekCompletion = weekDays.some((current) => completionKeys.has(current.key));
        planned = !weekCompletion ? index === 0 : completed;
      } else {
        const monthCompletion = (commitment.completions || []).some((completion) => completion?.completed);
        planned = !monthCompletion ? index === 0 : completed;
      }

      if (!planned && !completed) return null;
      const overdue = !completed && plannedTimePassed(commitment, day.key, now);
      const smallVersionTitle = String(commitment.minimum_version_title || "").trim();
      return {
        id: commitment.id,
        title: commitment.title || "Habit",
        shortLabel: commitment.title || "Habit",
        due: planned,
        completed,
        done: completed,
        overdue,
        planned_time: commitment.preferred_time || null,
        planned_time_label: formatPreferredTime(commitment.preferred_time),
        small_version_title: smallVersionTitle || null,
        semantic_type: commitment.semantic_profile?.commitment_type || null,
        primary_action_label: completed
          ? "Done"
          : overdue && smallVersionTitle
            ? "Keep it alive"
            : overdue
              ? "Start habit"
              : "Start",
        deeplink: `/today?habitId=${commitment.id}&date=${day.key}${overdue && smallVersionTitle ? "&mode=small_version" : ""}`,
        state: completed ? "done" : overdue ? "overdue" : "planned",
      };
    })
      .filter(Boolean);

    return {
      ...day,
      date: day.key,
      isSelected: selectedDateKey === day.key,
      dueCount: habits.length,
      doneCount: habits.filter((habit) => habit.completed).length,
      overdueCount: habits.filter((habit) => habit.overdue).length,
      habits,
      indicators: habits,
    };
  });
}

function weekStateHabitToPreview(habit, dayKey) {
  const status = String(habit.status || "");
  const completed = ["done", "small_version_done", "meal_checkin_done"].includes(status);
  const overdue = ["planned_time_passed", "overdue"].includes(status);
  const smallVersionTitle = String(habit.small_version_title || "").trim();
  return {
    id: habit.habit_id,
    title: habit.title || "Habit",
    shortLabel: habit.title || "Habit",
    due: status !== "not_due" && status !== "not_scheduled",
    completed,
    done: completed,
    overdue,
    planned_time: habit.planned_time || null,
    planned_time_label: weekStateStatusLabel(habit),
    small_version_title: smallVersionTitle || null,
    semantic_type: habit.semantic_type || null,
    primary_action_label: completed ? "Done" : overdue && smallVersionTitle ? "Keep it alive" : "Start habit",
    deeplink: habit.deeplink || `/today?habitId=${habit.habit_id}&date=${dayKey}${overdue && smallVersionTitle ? "&mode=small_version" : ""}`,
    state: completed ? "done" : overdue ? "overdue" : "planned",
  };
}

function weekStateStatusLabel(habit) {
  const status = String(habit?.status || "");
  const timeLabel = formatPreferredTime(habit?.planned_time);
  if (timeLabel && status === "pending") return `Planned ${timeLabel}`;
  return habit?.status_label || timeLabel || null;
}

export function buildProgressStory(goal) {
  if (!goal) return [];
  if (!hasNumericGoalTarget(goal)) return [];
  const unit = goal.unit ? ` ${goal.unit}` : "";
  const rows = [];
  const progressEntries = Array.isArray(goal.progress_entries)
    ? goal.progress_entries
        .filter((entry) => entry && entry.value !== null && entry.value !== undefined)
        .sort((left, right) => {
          const leftTime = new Date(left.created_at || 0).getTime();
          const rightTime = new Date(right.created_at || 0).getTime();
          return leftTime - rightTime;
        })
    : [];

  if (!progressEntries.length && !goal.completed_at) return [];

  if (
    progressEntries.length &&
    goal.starting_value !== null &&
    goal.starting_value !== undefined &&
    goal.starting_value !== ""
  ) {
    rows.push({
      id: "start",
      label: "Started",
      value: `${goal.starting_value}${unit}`.trim(),
      tone: "muted",
    });
  }

  if (progressEntries.length) {
    const latest = progressEntries[progressEntries.length - 1];
    const latestDate = formatTrustBudDateLabel(latest.created_at);
    rows.push({
      id: "latest-progress",
      label: latestDate ? `Logged ${latestDate}` : "Latest progress",
      value: `${goal.current_value ?? latest.value}${unit}`.trim(),
      tone: "active",
    });
  }

  if (
    goal.completed_at &&
    goal.target_value !== null &&
    goal.target_value !== undefined &&
    goal.target_value !== ""
  ) {
    rows.push({
      id: "target",
      label: "Reached",
      value: `${goal.target_value}${unit}`.trim(),
      tone: "success",
    });
  }

  return rows.slice(0, 3);
}

export function buildWhyText(goal, vision) {
  return (goal?.why_this_matters || vision?.description || "").trim();
}

export function buildVisionText(vision) {
  return (vision?.title || "").trim();
}
